import { useMutation } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { useState } from 'react';
import { contentToText, type AiAction, type DocumentContent, type RichNode, type Section } from '@imsenta/shared';
import { Alert, Button, Field, Modal, Select, Textarea } from '@/components/ui';
import { DocumentReadView } from '@/editor/DocumentEditor';
import { api, ApiError } from '@/lib/api';

const ACTIONS: { id: AiAction; label: string; hint: string }[] = [
  { id: 'draft_section', label: 'Draft this section', hint: 'Write a first draft from the section title.' },
  { id: 'expand', label: 'Add detail', hint: 'Make the existing text more specific and testable.' },
  { id: 'shorten', label: 'Tighten', hint: 'Cut length without losing any obligation.' },
  { id: 'rewrite', label: 'Rewrite', hint: 'Rework the text against your instruction.' },
  { id: 'review', label: 'Review for gaps', hint: 'List gaps and ambiguities as a reviewer would.' },
];

/**
 * Optional AI drafting.
 *
 * Nothing is written into the document until a person reads the suggestion and
 * chooses to insert it — the model drafts, the author remains the author.
 */
export function AiAssistPanel({
  section,
  documentId,
  documentType,
  onClose,
  onInsert,
}: {
  section: Section;
  documentId: string;
  documentType: string;
  onClose: () => void;
  onInsert: (nodes: RichNode[]) => void;
}) {
  const [action, setAction] = useState<AiAction>('draft_section');
  const [instruction, setInstruction] = useState('');
  const [tone, setTone] = useState<'formal' | 'plain' | 'concise'>('formal');
  const [result, setResult] = useState<{ markdown: string; content: DocumentContent; model: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sectionText = contentToText({ schemaVersion: 1, sections: [section] });

  const run = useMutation({
    mutationFn: () =>
      api.post<{ markdown: string; content: DocumentContent; model: string }>('/ai/assist', {
        action,
        documentId,
        sectionId: section.id,
        sectionTitle: section.title,
        documentType,
        input: action === 'draft_section' ? undefined : sectionText,
        instruction: instruction || undefined,
        tone,
      }),
    onSuccess: (data) => {
      setResult(data);
      setError(null);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'The assistant could not complete that request.');
      setResult(null);
    },
  });

  const insert = () => {
    if (!result) return;
    // Flatten the suggested sections into blocks appended to the current section.
    const nodes: RichNode[] = [];
    for (const suggested of result.content.sections) {
      if (suggested.title && suggested.title !== 'Introduction') {
        nodes.push({ type: 'heading', attrs: { level: 4 }, content: [{ type: 'text', text: suggested.title }] });
      }
      nodes.push(...suggested.body.content);
      for (const child of suggested.children) {
        nodes.push({ type: 'heading', attrs: { level: 5 }, content: [{ type: 'text', text: child.title }] });
        nodes.push(...child.body.content);
      }
    }
    onInsert(nodes.length ? nodes : [{ type: 'paragraph', content: [{ type: 'text', text: result.markdown }] }]);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <span className="inline-flex items-center gap-2">
          <Sparkles className="size-4 text-violet-600" />
          AI drafting assistant
        </span>
      }
      description={`Working on "${section.title}". Suggestions are drafts — review them before they become policy.`}
      width="xl"
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          {result ? (
            <Button variant="primary" onClick={insert}>
              Insert into section
            </Button>
          ) : (
            <Button variant="primary" loading={run.isPending} onClick={() => run.mutate()}>
              Generate
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="danger">{error}</Alert> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="What would you like?">
            <Select
              value={action}
              onChange={(e) => {
                setAction(e.target.value as AiAction);
                setResult(null);
              }}
            >
              {ACTIONS.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Tone">
            <Select value={tone} onChange={(e) => setTone(e.target.value as typeof tone)}>
              <option value="formal">Formal board register</option>
              <option value="plain">Plain English</option>
              <option value="concise">As concise as possible</option>
            </Select>
          </Field>
        </div>

        <p className="text-[12.5px] text-ink-500">{ACTIONS.find((a) => a.id === action)?.hint}</p>

        <Field label="Extra instruction" hint="Optional. For example: cover remote workers and third-party contractors.">
          <Textarea rows={2} value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder="Optional guidance for the assistant" />
        </Field>

        {result ? (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[11.5px] font-semibold tracking-wide text-ink-500 uppercase">Suggestion</p>
              <span className="text-[11.5px] text-ink-400">{result.model}</span>
            </div>
            <div className="max-h-80 overflow-y-auto rounded-lg border border-ink-200 bg-white px-4 py-3">
              <DocumentReadView content={result.content} />
            </div>
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                onClick={() => {
                  setResult(null);
                  run.mutate();
                }}
                loading={run.isPending}
              >
                Try again
              </Button>
              <Button size="sm" onClick={() => void navigator.clipboard.writeText(result.markdown)}>
                Copy Markdown
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
